import React, { useState, useContext } from 'react'
import classNames from 'classnames'
import Heading from '@toasttab/shared/src/components/molecules/Heading/Heading'
import MainForm from '@toasttab/onTheLine/src/components/Form/MainForm/MainForm'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import TextLink from '@toasttab/shared/src/components/TextLink/TextLink'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'

const MelCTA = ({ melCTA, parentToggle = null }: any) => {
  const [formToggle, setFormToggle] = useState(false)

  const otlParams = useContext(OTLContext)
  const isMinimal = otlParams.isMinimal

  const handleFormToggle = (e: { preventDefault: () => void }): any => {
    e.preventDefault()
    setFormToggle(!formToggle)
    if (parentToggle) {
      parentToggle(!formToggle)
    }
  }

  /* Begin -- CTA Default State on Page Load */
  const melCTAContainerCss = classNames({
    'ds-grid grid col-span-4 md:col-span-6 lg:col-span-12': true,
    hidden: formToggle,
    block: !formToggle
  })

  const melPictureCss = classNames({
    'col-span-4 md:col-span-1 lg:col-span-2 grid w-full md:w-[162px] text-left order-none sm:order-none':
      true
  })
  /* End -- CTA Default State on Page Load */

  /* Begin -- CTA Open State to show Form */
  const melCTAFormCss = classNames({
    'ds-grid grid col-span-12': true,
    block: formToggle,
    hidden: !formToggle
  })

  const formContainerCss = classNames({
    'col-span-4 md:col-span-5 lg:col-span-10 md:pl-6 w-full text-left': true
  })

  const formImgContainerCss = classNames({
    'flex justify-center md:hidden mb-4': true
  })

  const formImgCss = classNames({
    lazyload: true,
    'w-[100px] h-[98px]': true
  })
  /* End -- CTA Open State to show Form */

  /* Begin -- Global MELCTA Template */
  const melDesktopImgCSS = classNames({
    'h-[175px] w-unset lazyload': true
  })

  const melMobileImgCSS = classNames({
    'w-[92px] h-[100px] lazyload': true
  })

  const eyebrowIcon = {
    src: melCTA.gatedLabelIcon && melCTA.gatedLabelIcon[0]?.url,
    width: 14,
    height: 14,
    position: 'left'
  }
  /* End -- Global MELCTA Template */

  return (
    <div className='ds-grid px-4 py-4 md:py-5 mb-6 md:mb-0 mx-4 md:mx-8 lg:m-0  border-b border-gray-0 rounded-lg shadow-lg'>
      <div className={melCTAContainerCss}>
        {melCTA.gatedThumbnail && (
          <div className={`hidden md:block ${melPictureCss}`}>
            <picture className=''>
              {melCTA.gatedThumbnail[0].optimizedImages.src && (
                <source
                  srcSet={melCTA.gatedThumbnail[0].optimizedImages.src}
                  itemProp='image'
                  title={melCTA.gatedThumbnail[0].title}
                />
              )}
              {melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp && (
                <source
                  srcSet={melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp}
                  sizes='100vw'
                  type='image/webp'
                />
              )}
              <img
                src={melCTA.gatedThumbnail[0].optimizedImages.src}
                alt={melCTA.gatedThumbnail[0].title}
                itemProp='image'
                className={melDesktopImgCSS}
              />
            </picture>
          </div>
        )}
        <div className='col-span-4 md:col-span-5 lg:col-span-10 md:pl-6 w-full text-left order-none sm:order-none'>
          <Eyebrow
            icon={eyebrowIcon}
            componentClass={`text-default text-xs tracking-[0.1em] ${
              isMinimal ? 'mb-3' : 'mb-4'
            }`}
          >
            {melCTA.gatedLabel}
          </Eyebrow>

          {melCTA.gatedThumbnail && (
            <div
              className={
                isMinimal
                  ? `hidden md:block md:hidden md:flex md:justify-center ${melPictureCss} mb-4`
                  : `block md:hidden flex justify-center ${melPictureCss} mb-4`
              }
            >
              <picture className=''>
                {melCTA.gatedThumbnail[0].optimizedImages.src && (
                  <source
                    srcSet={melCTA.gatedThumbnail[0].optimizedImages.src}
                    itemProp='image'
                    title={melCTA.gatedThumbnail[0].title}
                  />
                )}
                {melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp && (
                  <source
                    srcSet={melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp}
                    sizes='100vw'
                    type='image/webp'
                  />
                )}
                <img
                  src={melCTA.gatedThumbnail[0].optimizedImages.src}
                  alt={melCTA.gatedThumbnail[0].title}
                  itemProp='image'
                  className={melMobileImgCSS}
                />
              </picture>
            </div>
          )}
          <Heading
            allowHtml
            heading='h3'
            content={melCTA.gatedTitle}
            fontFamily='font-calibre'
            fontWeight='font-semibold'
            fontSize={['text-xl', 'md:text-3xl']}
          />

          <Typography
            as='p'
            variation='default'
            classes={
              isMinimal
                ? 'hidden md:block font-sansPro text-base text-secondary md:my-4'
                : 'font-sansPro text-base text-secondary my-4'
            }
            allowHtml={false}
          >
            {melCTA.gatedText}
          </Typography>

          <Button
            variation='primary'
            classes={
              isMinimal
                ? 'hidden md:inline-flex lg:w-full md:!w-[194px] md:mb-4'
                : 'w-full md:!w-[194px] mb-4'
            }
            onClick={handleFormToggle}
          >
            <span className={`font-semibold`}>Get Free Download</span>
          </Button>

          <TextLink
            href='#'
            text='Get free download'
            underline={false}
            classes={isMinimal ? 'inline md:hidden' : 'hidden'}
            color='default'
            fontWeight={['font-semibold']}
            icon='arrow'
            onClick={handleFormToggle}
          />

          <div className='block'>
            <img
              className='h-[11px] inline'
              src={resolvePrefix('/svg/img-toast-logo.svg')}
              alt='Toast'
            />
            <span className='text-xs text-brand-50 uppercase inline'>
              {' '}
              | BUILT FOR RESTAURANTS
            </span>
          </div>
        </div>
      </div>

      <div className={melCTAFormCss}>
        {melCTA.gatedThumbnail && (
          <div className={`hidden md:block ${melPictureCss}`}>
            <picture className=''>
              {melCTA.gatedThumbnail[0].optimizedImages.src && (
                <source
                  srcSet={melCTA.gatedThumbnail[0].optimizedImages.src}
                  itemProp='image'
                  title={melCTA.gatedThumbnail[0].title}
                />
              )}
              {melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp && (
                <source
                  srcSet={melCTA.gatedThumbnail[0].optimizedImages.srcsetWebp}
                  sizes='100vw'
                  type='image/webp'
                />
              )}
              <img
                src={melCTA.gatedThumbnail[0].optimizedImages.src}
                alt={melCTA.gatedThumbnail[0].title}
                itemProp='image'
                className={melDesktopImgCSS}
              />
            </picture>
          </div>
        )}
        <div className={formContainerCss}>
          <div className='flex justify-between'>
            <Eyebrow
              icon={eyebrowIcon}
              componentClassName='text-default text-xs tracking-[0.1em] mb-4'
            >
              {melCTA.gatedLabel}
            </Eyebrow>
            {Boolean(formToggle) && Boolean(isMinimal) && (
              <button
                className='w-4 h-4 md:hidden'
                aria-label='Toggle Form Open & Close'
                aria-controls='mobile-nav-menu'
                onClick={handleFormToggle}
              >
                <span
                  aria-hidden='true'
                  className='block absolute h-0.5 w-4 bg-gray-125 transform rotate-45'
                ></span>
                <span
                  aria-hidden='true'
                  className='block absolute h-0.5 w-4 bg-gray-125 transform -rotate-45'
                ></span>
              </button>
            )}
          </div>

          {melCTA.gatedThumbnail && (
            <div className={formImgContainerCss}>
              <picture>
                <source
                  srcSet={melCTA.gatedThumbnail[0].optimizedImages.srcWebp}
                  type='image/webp'
                />
                <img
                  className={melMobileImgCSS}
                  src={melCTA.gatedThumbnail[0].url}
                  alt={melCTA.gatedThumbnail[0].title}
                />
              </picture>
            </div>
          )}
          <Heading
            allowHtml
            heading='h3'
            classes='mb-4'
            content={melCTA.gatedTitle}
            fontFamily='font-calibre'
            fontWeight='font-semibold'
            fontSize={['text-xl', 'md:text-3xl']}
          />
          <Typography
            as='p'
            variation='default'
            classes='font-sansPro text-base text-secondary mb-4'
            allowHtml={false}
          >
            {melCTA.gatedText}
          </Typography>
          {melCTA.formFieldset && (
            <MainForm
              entryId={'1'}
              typeHandle={melCTA.typeHandle}
              formId={melCTA.marketoFormId}
              programName={melCTA.marketoProgramName}
              formCategory={melCTA.formFieldset[0]}
              redirectUrl={melCTA.ctaThankYouLink}
              buttonText='Get a free download'
              buttonStyle='secondary-otl'
              conversionType='MEL-HEADLESS'
              mds={true}
            />
          )}
          <div className='block'>
            <img
              className='h-[11px] inline'
              src={resolvePrefix('/svg/img-toast-logo.svg')}
              alt='Toast'
            ></img>
            <span className='text-xs text-brand-50 uppercase tracking-[0.1em] inline'>
              {' '}
              | BUILT FOR RESTAURANTS
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MelCTA
